import { Injectable } from '@angular/core';
import { AUTH_ACCESS_TOKEN_KEY, AUTH_REFRESH_TOKEN_KEY, AuthToken } from './auth.config';

/**
 * Where the session's tokens live (task-modernize-internal-apps §1.2, step 1).
 *
 * Both tokens are in localStorage, and expiry is honoured on read: an expired token reads as
 * none, the way a cookie's own expiry used to make it vanish.
 *
 * **The access token was memory-only until 2026-09-09** (VT), which meant every hard reload spent
 * a refresh round trip before the first call could go out — and on `/salons`, which opens two
 * calls in one tick, that round trip is on the critical path of the page. The security argument
 * for holding it in memory does not stand while the REFRESH token is in localStorage beside it:
 * a script that can read one can read the other, and the refresh token is the more powerful of
 * the two — it mints access tokens for as long as it lives. Keeping the weaker one out bought
 * nothing.
 *
 * It becomes worth doing again at step 2 of that ticket, when identity sets the refresh token as
 * an httpOnly cookie and Juno never holds it. At that point the access token should go back to
 * memory — refresh in an httpOnly cookie, access in memory is the shape worth having — and this
 * is the change to reverse: delete the two `localStorage` lines below and the field is private
 * again.
 */
@Injectable({ providedIn: 'root' })
export class TokenStore {
  /** the live access token, or null when there is none or it has expired */
  accessToken(): AuthToken | null {
    return this.read(AUTH_ACCESS_TOKEN_KEY);
  }

  /** the refresh token, or null when there is none or it has expired */
  refreshToken(): AuthToken | null {
    return this.read(AUTH_REFRESH_TOKEN_KEY);
  }

  /** both tokens as identity returned them, `expiresAt` already in ms */
  set(access: AuthToken, refresh: AuthToken): void {
    localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, JSON.stringify(access));
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, JSON.stringify(refresh));
  }

  clear(): void {
    localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  }

  /**
   * A token is only what it says while it is still valid. Anything else — unreadable storage, a
   * half-written value, the wrong shape, an expired stamp — reads as no token, which sends the
   * caller down the refresh path rather than throwing on a page load.
   *
   * And what cannot be used is swept, so a bad value is a one-time refresh and not a permanent
   * one: left in place it would be re-parsed and re-rejected on every request for the life of
   * the browser profile. This is also what the cookies it replaced did on their own — an expired
   * cookie simply vanished.
   */
  private read(key: string): AuthToken | null {
    let token: AuthToken;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      token = JSON.parse(raw) as AuthToken;
    } catch {
      // storage is unreadable, or the value is not JSON
      return this.discard(key);
    }
    // written by an older shape, or half-written: not a token, whatever it parsed to
    if (typeof token?.token !== 'string' || typeof token?.expiresAt !== 'number') return this.discard(key);
    return token.expiresAt > Date.now() ? token : this.discard(key);
  }

  /**
   * Drops one key and answers "no token". Only the key that failed: a bad ACCESS token must not
   * take the refresh token with it, or a value nobody can parse becomes a forced sign-out
   * instead of one extra refresh.
   */
  private discard(key: string): null {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage itself is gone (a private window, a browser set to block site data): there is
      // nothing to sweep, and the null below is still the right answer
    }
    return null;
  }
}
